import type {
  AcceptInvitationInput,
  ChangePasswordInput,
  ConfirmEmailChangeInput,
  ConfirmTotpInput,
  emailChangedDto,
  emailChangeRequestedDto,
  externalMeDto,
  FinishPasskeyLoginBody,
  factorAddedDto,
  factorRemovedDto,
  factorsDto,
  LoginWithPasswordInput,
  passkeyCreationOptionsDto,
  passkeyRequestOptionsDto,
  passwordChangedDto,
  passwordResetDto,
  passwordResetRequestedDto,
  ReauthenticateInput,
  RegisterPasskeyBody,
  RemoveFactorInput,
  RequestEmailChangeBody,
  RequestPasswordResetBody,
  ResetPasswordInput,
  reauthenticatedDto,
  recoveryCodesDto,
  sessionClosedDto,
  sessionsDto,
  signedOutDto,
  signInOutcomeDto,
  totpEnrollmentDto,
} from "@nexxt/contracts/identity";
import type {
  notificationPreferenceDto,
  notificationPreferencesDto,
  SetMyNotificationPreferenceInput,
} from "@nexxt/contracts/notifications";
import type { ownProfileDto, UpdateOwnProfileInput } from "@nexxt/contracts/party";
import type { Product } from "@nexxt/ui";
import type * as z from "zod";

// What the sign-in and account screens of the external users ask of a gateway (ADR-207): one call per route of the
// access of the externals (identity's ExternalAuthService, ADR-148), the own profile (party) and the own emails
// (notifications), with the input and the answer of the contract. Each external app gives its own, on the client of
// its gateway, and the screens never see a route.

export type SignInOutcome = z.output<typeof signInOutcomeDto>;
export type ExternalMe = z.output<typeof externalMeDto>;
export type TotpEnrollment = z.output<typeof totpEnrollmentDto>;

/** The token of the page's Turnstile widget, which the app sends in the header the gateway reads (ADR-148). */
type Checked<I, R> = (input: I, turnstile: string) => Promise<R>;
type Call<I, R> = (input: I) => Promise<R>;
type Read<R> = () => Promise<R>;

/**
 * The operations of the screens. The roads in (up to `confirmEmailChange`) go on the client of the sign-in pages,
 * where a 401 is a refused credential; the others on the client of the signed-in pages, where it is a session that
 * ended. Names are those of identity's RPC methods, but `verifyRecoveryCode` (identity's `useRecoveryCode`, which
 * would read as a React hook).
 */
export interface AccountClient {
  loginWithPassword: Checked<LoginWithPasswordInput, SignInOutcome>;
  verifyTotp: Call<{ code: string }, SignInOutcome>;
  verifyRecoveryCode: Call<{ code: string }, SignInOutcome>;
  beginPasskeyLogin: Read<z.output<typeof passkeyRequestOptionsDto>>;
  finishPasskeyLogin: Call<FinishPasskeyLoginBody, SignInOutcome>;
  requestPasswordReset: Checked<RequestPasswordResetBody, z.output<typeof passwordResetRequestedDto>>;
  resetPassword: Checked<ResetPasswordInput, z.output<typeof passwordResetDto>>;
  acceptInvitation: Checked<AcceptInvitationInput, SignInOutcome>;
  confirmEmailChange: Checked<ConfirmEmailChangeInput, z.output<typeof emailChangedDto>>;
  getMe: Read<ExternalMe>;
  logout: Read<z.output<typeof signedOutDto>>;
  logoutEverywhere: Read<z.output<typeof signedOutDto>>;
  reauthenticate: Call<ReauthenticateInput, z.output<typeof reauthenticatedDto>>;
  listFactors: Read<z.output<typeof factorsDto>>;
  enrollTotp: Read<TotpEnrollment>;
  confirmTotp: Call<ConfirmTotpInput, z.output<typeof factorAddedDto>>;
  beginPasskeyRegistration: Read<z.output<typeof passkeyCreationOptionsDto>>;
  registerPasskey: Call<RegisterPasskeyBody, z.output<typeof factorAddedDto>>;
  removeFactor: Call<RemoveFactorInput, z.output<typeof factorRemovedDto>>;
  regenerateRecoveryCodes: Read<z.output<typeof recoveryCodesDto>>;
  changePassword: Call<ChangePasswordInput, z.output<typeof passwordChangedDto>>;
  listSessions: Read<z.output<typeof sessionsDto>>;
  closeSession: Call<{ sessionId: string }, z.output<typeof sessionClosedDto>>;
  requestEmailChange: Call<RequestEmailChangeBody, z.output<typeof emailChangeRequestedDto>>;
  updateOwnProfile: Call<UpdateOwnProfileInput, z.output<typeof ownProfileDto>>;
  listMyNotificationPreferences: Read<z.output<typeof notificationPreferencesDto>>;
  setMyNotificationPreference: Call<SetMyNotificationPreferenceInput, z.output<typeof notificationPreferenceDto>>;
  /** This browser stops receiving pushes, before signing out (ADR-283); absent in an app with no pushes (Frontrow). */
  unsubscribePush?: Call<{ endpoint: string }, unknown>;
}

/** An app of the external users: Backstage and Guestlist sign in through Logto (ADR-024, ADR-294), with none of these screens. */
export type ExternalProduct = Exclude<Product, "backstage" | "guestlist">;

export interface AccountApp {
  client: AccountClient;
  /** The lockup of the pages outside the frame, and the name the pages say. */
  product: ExternalProduct;
  /** The site key of the app's Turnstile widget for this stage (ADR-154 point 8); empty where there is none. */
  turnstileSiteKey: string;
}

let registered: AccountApp | undefined;

/** Once, when the app starts: before it, no sign-in or account screen loads (the route loaders read through it too). */
export function registerAccountApp(app: AccountApp): void {
  registered = app;
}

export function accountApp(): AccountApp {
  if (!registered) throw new Error("registerAccountApp was not called when the app started");
  return registered;
}

export const accountClient = (): AccountClient => accountApp().client;

// The name of a product is the brand, the same in every language (ADR-102).
const productNames: Record<ExternalProduct, string> = { atelier: "Atelier", frontrow: "Frontrow" };

/** "Atelier", "Frontrow": what the pages call the app. */
export const productName = (): string => productNames[accountApp().product];
